/**
 * ConfigBridgeService
 *
 * Serializes editor scene state into a flat GameConfigJSON object
 * and writes it to `slot-core/game.config.json` via FileSystemService.
 *
 * This is the single source of truth for translating the editor's
 * runtime data (sceneStore GameObjects + components) into the config
 * format consumed by the slot-core game engine.
 */

import { useSceneStore } from '@store/sceneStore';
import { writeFile, fileExists } from '@renderer/services/FileSystemService';
import { convertBackendToSlotConfig } from '@renderer/services/backend-game/ConfigConverter';
import { resolvePartData, DEFAULT_CASCADE_SLOT_CONFIG } from '@core/types/cascade-slot.types';
import type { CascadeSlotConfig, CascadeSlotPartData } from '@core/types/cascade-slot.types';
import type { BackendGameComponentData } from '@core/types/backend-game.types';
import type { GameObject, Component } from '@core/types/gameobject.types';
import type { GameConfigJSON, GameConfigUIPartLayout } from '@core/types/game-config.types';
import { DEFAULT_GAME_CONFIG } from '@core/types/game-config.types';
import { SLOT_CORE_DEST_FOLDER } from '@renderer/constants/slot-core';

// ============================================================================
// Constants
// ============================================================================

const CONFIG_FILE_PATH = `${SLOT_CORE_DEST_FOLDER}/game.config.json`;
const DEBOUNCE_MS = 150;
const INTEGRATION_API_BASE = 'https://www.integration-api.net/games/';

// ============================================================================
// Internal helpers
// ============================================================================

/** Find a component by type on a GameObject */
function findComponent(go: GameObject, type: string): Component | undefined {
  return go.components.find(c => c.type === type);
}

/** Extract CascadeSlotConfig from the slot root GameObject */
function extractSlotConfig(
  slotRoot: GameObject
): CascadeSlotConfig {
  // Backend game path — has a backend-game-config component
  const backendComp = findComponent(slotRoot, 'backend-game-config');
  if (backendComp) {
    const data = backendComp.data as unknown as BackendGameComponentData;
    return convertBackendToSlotConfig(data);
  }

  // Local cascade-slot path — has a cascade-slot-config component
  const cascadeComp = findComponent(slotRoot, 'cascade-slot-config');
  if (cascadeComp) {
    return {
      ...DEFAULT_CASCADE_SLOT_CONFIG,
      ...(cascadeComp.data as Partial<CascadeSlotConfig>),
    };
  }

  return { ...DEFAULT_CASCADE_SLOT_CONFIG };
}

/** Collect UI layout from child GameObjects that have cascade-slot-part components */
function collectUILayout(
  slotRoot: GameObject,
  gameObjects: Record<string, GameObject>
): Record<string, GameConfigUIPartLayout> {
  const layout: Record<string, GameConfigUIPartLayout> = {};

  const childIds = slotRoot.childIds ?? [];
  const queue = [...childIds];

  while (queue.length > 0) {
    const id = queue.shift()!;
    const child = gameObjects[id];
    if (!child) continue;

    // Enqueue grandchildren
    if (child.childIds) {
      queue.push(...child.childIds);
    }

    const partComp = findComponent(child, 'cascade-slot-part');
    if (!partComp) continue;

    const partData: CascadeSlotPartData = resolvePartData(partComp.data);
    const partKey = partData.partType;

    layout[partKey] = {
      visible: partData.visible ?? true,
      offsetX: partData.offsetX ?? 0,
      offsetY: partData.offsetY ?? 0,
      customWidth: partData.customWidth ?? null,
      customHeight: partData.customHeight ?? null,
      anchorX: partData.anchorX,
      anchorY: partData.anchorY,
      fontSize: partData.fontSize,
      fontColor: partData.fontColor,
      fontFamily: partData.fontFamily,
      fontWeight: partData.fontWeight,
      textContent: partData.textContent,
      textAlign: partData.textAlign,
      bgColor: partData.bgColor,
      textColor: partData.textColor,
      borderRadius: partData.borderRadius,
      textureAssetPath: partData.textureAssetPath,
      textureFit: partData.textureFit as 'cover' | 'contain' | 'fill' | undefined,
      symbolScale: partData.symbolScale,
      gridPadding: partData.gridPadding,
    };
  }

  return layout;
}

/** Build the optional API config section for backend games */
function buildApiConfig(slotRoot: GameObject): GameConfigJSON['api'] | undefined {
  const backendComp = findComponent(slotRoot, 'backend-game-config');
  if (!backendComp) return undefined;

  const data = backendComp.data as unknown as BackendGameComponentData;
  if (!data.gameId) return undefined;

  return {
    enabled: data.liveApiEnabled ?? false,
    gameId: data.gameId,
    integrationUrl: INTEGRATION_API_BASE,
  };
}

/** Resolve background config from the game_background part or fallback */
function resolveBackground(
  uiLayout: Record<string, GameConfigUIPartLayout>
): GameConfigJSON['background'] {
  const bgPart = uiLayout['game_background'];
  return {
    color: bgPart?.bgColor ?? DEFAULT_GAME_CONFIG.background.color,
    imagePath: bgPart?.textureAssetPath ?? undefined,
    imageFit: bgPart?.textureFit ?? undefined,
  };
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Build a GameConfigJSON from the current scene state.
 *
 * Returns `null` if no slot game root is found in the scene.
 */
export function buildConfigFromScene(): GameConfigJSON | null {
  const { gameObjects } = useSceneStore.getState();

  // Find the slot root GameObject
  const slotRoot = Object.values(gameObjects).find(
    go => go.type === 'cascade-slot' || go.type === 'backend-slot-game'
  );
  if (!slotRoot) return null;

  // Extract config from the slot root's components
  const slotConfig = extractSlotConfig(slotRoot);

  // Collect UI part layouts from children
  const uiLayout = collectUILayout(slotRoot, gameObjects);

  // Build the API config section (backend games only)
  const api = buildApiConfig(slotRoot);

  // Determine game identity
  const backendComp = findComponent(slotRoot, 'backend-game-config');
  const backendData = backendComp
    ? (backendComp.data as unknown as BackendGameComponentData)
    : null;

  const gameId = backendData?.gameId ?? slotRoot.id;
  const gameName = backendData?.name ?? slotRoot.name ?? 'Untitled Slot Game';

  // Assemble the full config
  const config: GameConfigJSON = {
    version: 1,

    game: {
      id: gameId,
      name: gameName,
      canvasWidth: DEFAULT_GAME_CONFIG.game.canvasWidth,
      canvasHeight: DEFAULT_GAME_CONFIG.game.canvasHeight,
    },

    grid: {
      rows: slotConfig.rows,
      columns: slotConfig.columns,
      ...(slotConfig.reelHeights ? { reelHeights: slotConfig.reelHeights } : {}),
      winType: slotConfig.winType ?? 'count',
      symbolWidth: slotConfig.symbolWidth,
      symbolHeight: slotConfig.symbolHeight,
      symbolSpacing: slotConfig.symbolSpacing,
    },

    symbols: slotConfig.symbols.map(sym => ({
      id: sym.id,
      name: sym.name,
      category: sym.category,
      color: sym.color,
      textColor: sym.textColor,
      weight: sym.weight,
      freeGameWeight: sym.freeGameWeight,
      paytable: sym.paytable.map(p => ({
        minCount: p.minCount,
        maxCount: p.maxCount,
        payout: p.payout,
      })),
      displayMode: sym.displayMode,
      ...(sym.textureAssetPath ? { textureAssetPath: sym.textureAssetPath } : {}),
      ...(sym.displayMode === 'spine' && sym.spineConfig ? {
        spineConfig: {
          skeletonPath: sym.spineConfig.skeletonPath,
          atlasPath: sym.spineConfig.atlasPath,
          texturePaths: sym.spineConfig.texturePaths,
          animations: Object.fromEntries(
            Object.entries(sym.spineConfig.animations).filter(([, v]) => v != null && v !== '')
          ),
          ...(sym.spineConfig.defaultSkin ? { defaultSkin: sym.spineConfig.defaultSkin } : {}),
          timeScale: sym.spineConfig.timeScale ?? 1,
        },
      } : {}),
    })),

    cascade: {
      enabled: slotConfig.cascadeEnabled,
      removeDuration: slotConfig.cascadeRemoveDuration,
      fillDuration: slotConfig.cascadeFillDuration,
      gravityDuration: slotConfig.cascadeGravityDuration,
      columnDelay: slotConfig.cascadeColumnDelay,
      fallStyle: slotConfig.cascadeFallStyle,
      winHighlightScale: slotConfig.winHighlightScale,
    },

    multiplierPool: slotConfig.multiplierPool.map(m => ({
      value: m.value,
      weight: m.weight,
    })),

    freeSpins: {
      triggerSymbolId: slotConfig.freeSpins.triggerSymbolId,
      triggerCount: slotConfig.freeSpins.triggerCount,
      spinsAwarded: slotConfig.freeSpins.spinsAwarded,
      retriggerCount: slotConfig.freeSpins.retriggerCount,
      retriggerSpinsAwarded: slotConfig.freeSpins.retriggerSpinsAwarded,
    },

    economy: {
      betLevels: [...slotConfig.betLevels],
      startingBalance: slotConfig.startingBalance,
      buyFeatureEnabled: slotConfig.buyFeatureEnabled,
      buyFeatureCostMultiplier: slotConfig.buyFeatureCostMultiplier,
      maxWinMultiplier: slotConfig.maxWinMultiplier,
    },

    spinAnimation: {
      spinDuration: slotConfig.spinDuration,
      turboMultiplier: slotConfig.turboMultiplier,
      winPresentDuration: slotConfig.winPresentDuration,
    },

    uiLayout,

    background: resolveBackground(uiLayout),

    ...(api ? { api } : {}),
  };

  return config;
}

// ============================================================================
// Write & Debounce
// ============================================================================

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Write the current scene config to `slot-core/game.config.json`.
 *
 * Creates intermediate directories if needed (handled by FileSystemService.writeFile).
 * Silently skips if no slot game root is found or if slot-core is not installed.
 */
export async function writeConfig(): Promise<void> {
  // Only write if slot-core directory exists (i.e. is installed)
  const installed = await fileExists(`${SLOT_CORE_DEST_FOLDER}/.version`);
  if (!installed) return;

  const config = buildConfigFromScene();
  if (!config) return;

  const json = JSON.stringify(config, null, 2);
  await writeFile(CONFIG_FILE_PATH, json);

  if (process.env.NODE_ENV === 'development') {
    console.log('[ConfigBridge] Wrote game.config.json');
  }
}

/**
 * Schedule a debounced config write (150ms).
 *
 * Multiple rapid calls collapse into a single write.
 */
export function scheduleConfigWrite(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    writeConfig().catch(err => {
      console.warn('[ConfigBridge] Failed to write config:', err);
    });
  }, DEBOUNCE_MS);
}
